import React from "react";
import { MenuIcon } from "@heroicons/react/solid";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectAuth } from "../../store/auth_slice";
import images from "./assets/images";
import Button from "../button/button";
import Dropdown from "./dropdown/dropdown";
import Avatar from "../avatar/avatar";
import LoginPage from "../../pages/Login/login";
import SignUpPage from "../../pages/signup/signup";
import InformationModal from "../modal/information_modal";

interface HeaderProps {
  className?: string;
}

const Header: React.FC<HeaderProps> = ({ className = "" }) => {
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const [isMobileDropdownOpen, setIsMobileDropdownOpen] = React.useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = React.useState(false);
  const [isSignUpModalOpen, setIsSignUpModalOpen] = React.useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = React.useState(false);

  const navigate = useNavigate();
  const { isAuthenticated, user } = useSelector(selectAuth);

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const toggleMobileDropdown = () => {
    setIsMobileDropdownOpen(!isMobileDropdownOpen);
  };

  const openLoginModal = () => {
    setIsLoginModalOpen(true);
    setIsDropdownOpen(false);
  };

  const closeLoginModal = () => {
    setIsLoginModalOpen(false);
  };

  const openSignUpModal = () => {
    setIsSignUpModalOpen(true);
    setIsDropdownOpen(false);
  };

  const closeSignUpModal = () => {
    setIsSignUpModalOpen(false);
  };

  const goToDroneService = () => {
    if (!isAuthenticated) {
      setIsInfoModalOpen(true);
      return;
    }
    
    if (!user?.stripeOnboardingComplete) {
      setIsInfoModalOpen(true);
      return;
    }

    navigate("/drone-service");
  };

  const handleLogoClick = () => {
    navigate("/");
  };

  const handleStripeSetup = () => {
    setIsInfoModalOpen(false);
    navigate("/account");
  };

  return (
    <div
      className={`relative flex justify-between items-center w-full z-50 ${className}`}
    >
      <div className="flex items-center">
        <img
          src={images.DroneDenLogo}
          alt="DroneDen Logo"
          className="h-8 md:h-12 w-[192px] cursor-pointer"
          onClick={handleLogoClick}
        />
      </div>

      {/* Elemente care se afișează doar pe ecrane mari */}
      <div className="hidden md:flex items-center space-x-6 font-ubuntu text-xl">
        <Button type="button" onClick={goToDroneService}>
          Rent Your Services
        </Button>
        {/* <LocaleSwitch /> */}

        <div className="relative">
          <div
            className={`flex space-x-6 items-center cursor-pointer px-4 py-1 rounded-lg transition-colors duration-300 ${
              isDropdownOpen ? "bg-grayOne" : "bg-transparent hover:bg-grayOne"
            }`}
            onClick={toggleDropdown}
          >
            <MenuIcon className="h-6 w-6 text-gray-700" />
            <Avatar size="medium" />
          </div>

          {/* Dropdown care include opțiunea de Login și Sign up */}
          <Dropdown
            isOpen={isDropdownOpen}
            openLoginModal={openLoginModal}
            openSignUpModal={openSignUpModal}
          />
        </div>
      </div>

      {/* Hamburger button pentru ecrane mici */}
      <div className="block md:hidden">
        <button
          className="flex items-center space-x-2 px-4 py-2 bg-gray-200 rounded-lg"
          onClick={toggleMobileDropdown}
        >
          <MenuIcon className="h-6 w-6 text-gray-700" />
        </button>

        {isMobileDropdownOpen && (
          <div className="absolute right-0 mt-2 w-48 bg-white shadow-lg rounded-lg">
            <ul className="py-1">
              <li
                className="cursor-pointer px-4 py-2 hover:bg-gray-100"
                onClick={() => {
                  goToDroneService();
                  setIsMobileDropdownOpen(false);
                }}
              >
                Rent Your Services
              </li>
              <li className="border-t my-1" />
              <Dropdown
                isOpen={isMobileDropdownOpen}
                openLoginModal={() => {
                  openLoginModal();
                  setIsMobileDropdownOpen(false);
                }}
                openSignUpModal={() => {
                  openSignUpModal();
                  setIsMobileDropdownOpen(false);
                }}
              />
            </ul>
          </div>
        )}
      </div>

      <InformationModal
        isOpen={isInfoModalOpen}
        onClose={() => setIsInfoModalOpen(false)}
        message={!isAuthenticated 
          ? "You need to be logged in to post a service. Please log in or sign up to continue."
          : "You need to complete your Stripe account setup before posting a service. Please set up your payment information to continue."}
        title={!isAuthenticated ? "Login Required" : "Stripe Account Required"}
        secondaryButton={isAuthenticated && !user?.stripeOnboardingComplete ? {
          label: "Go to Account Settings",
          onClick: handleStripeSetup
        } : undefined}
      />

      <LoginPage
        isOpen={isLoginModalOpen}
        onClose={closeLoginModal}
        openSignUpModal={openSignUpModal}
      />

      <SignUpPage
        isOpen={isSignUpModalOpen}
        onClose={closeSignUpModal}
        openLoginModal={openLoginModal}
      />
    </div>
  );
};

export default Header;
